import React, { useRef } from 'react';
import { connect } from 'react-redux';

import { formatTariffIntervals } from 'apps/promo/components/calculator/CalculatorUtils';
import { LightDangerouslyHtml } from 'apps/promo/components/common/fromAuth/LightDangerouslyHtml/LightDangerouslyHtml';
import { PromoIcon } from 'apps/promo/components/common/promo-icon/PromoIcon';
import { PromoText } from 'apps/promo/components/common/promo-text/PromoText';
import PromoPopup from 'apps/promo/components/popup/PromoPopup';
import PromoPopupButton from 'apps/promo/components/popup/PromoPopupButton';
import PromoSimplePopup from 'apps/promo/components/popup/PromoSimplePopup';
import { RULES } from 'apps/promo/constants/UrlConstants';
import { getLocalizedRawAmount } from 'apps/promo/helpers/formats';
import { getLangCountryPrefix } from 'apps/promo/helpers/utils';
import { useApp, useLanguage } from 'apps/promo/hooks/selectors';

import { FeeTable, FeeTableRightBlock, FeeTableRow, FeeTableSeparator } from './styles';

const FeeBlock = (props) => {
    const { country } = useApp();
    const { lang } = useLanguage();
    const popupRef = useRef(null);
    const messages = props.calculatorData.messages;

    const SEPARATOR = ':';
    const { sourceCurrency, targetCurrency } = props.calculatorData;
    const { captions, commissionCurrency } = props.calculatorData.transaction;

    let [processingTimeCaption, processingTimeEstimationCaption] = captions.processingTime.includes(SEPARATOR)
        ? captions.processingTime.split(SEPARATOR)
        : ['', ''];

    if (processingTimeCaption && processingTimeCaption) {
        processingTimeCaption = processingTimeCaption.trim();
        processingTimeEstimationCaption = processingTimeEstimationCaption.trim();

        processingTimeCaption = processingTimeCaption.charAt(0).toUpperCase() + processingTimeCaption.slice(1);
        processingTimeEstimationCaption = processingTimeEstimationCaption.charAt(0).toUpperCase() + processingTimeEstimationCaption.slice(1);

        processingTimeCaption += SEPARATOR;
    }

    const bonusSpecialRate = props.calculatorData.transaction.bonusSpecialRate;
    const bonusSpecialCommission = props.calculatorData.transaction.bonusSpecialCommission;
    const hasBonusSpecialCommission = bonusSpecialCommission !== null;

    const feeAmount = props.commission.toFixed(2);
    const fee =
        `${getLocalizedRawAmount(lang, country, hasBonusSpecialCommission ? bonusSpecialCommission : feeAmount)} ${commissionCurrency ? commissionCurrency : sourceCurrency}`.trim();

    const isRequestOperation = props.calculatorData.operation === 'request';

    const requestAmount = Number.isNaN(props.requestAmount) ? feeAmount : Number(props.requestAmount).toFixed(2);
    const requestAmountTotal = `${getLocalizedRawAmount(lang, country, requestAmount)} ${props.requestCurrency ? props.requestCurrency : ''}`;

    const termsHref = `${getLangCountryPrefix(props.languageCode, props.countryCode)}${RULES}`;

    const formatTariffBlock = (tariffs, label) => {
        if (!Array.isArray(tariffs) || !tariffs.length) return null;

        const tariffTexts = formatTariffIntervals(
            tariffs,
            messages.tariffIntervalPercent,
            messages.tariffIntervalPercentFirst,
            messages.tariffIntervalPercentLast,
            messages.tariffIntervalFix,
            messages.tariffIntervalFixFirst,
            messages.tariffIntervalFixLast,
            commissionCurrency,
            lang,
            country,
        );

        return `<p>${label}<br/> ${tariffTexts.map((text) => `${text}</br>`).join('')}</p>`;
    };

    const getSendFeePopupMessage = (data) => {
        const { appliedTariffFrom, appliedTariffTo } = data || {};

        const parts = [
            formatTariffBlock(appliedTariffTo, messages.sendFeePopupSendingFeeText),
            formatTariffBlock(appliedTariffFrom, messages.sendFeePopupAdditionalFeeText),
            `<p>${messages.sendFeePopupDisclaimer}</p>`,
        ];

        return parts.filter(Boolean).join('');
    };

    const sendFeeText = getSendFeePopupMessage(props.calculatorData.transaction);

    let improvedFeePopupText = messages.improvedFeePopupText;
    const improvedFeeValues = {
        CURRENCY_FROM: sourceCurrency,
        CURRENCY_TO: targetCurrency,
        FEE: hasBonusSpecialCommission ? bonusSpecialCommission : props.commission,
    };
    for (const key in improvedFeeValues) {
        improvedFeePopupText = improvedFeePopupText?.replaceAll(`{${key}}`, improvedFeeValues[key]);
    }

    const isClickableFeeText = isRequestOperation || sendFeeText || hasBonusSpecialCommission;

    const setHTML = (val) => <LightDangerouslyHtml dangerouslyHtml={val} />;

    const feePopup = (val) => {
        popupRef.current?.open(
            <PromoSimplePopup text={setHTML(val)} popupRef={popupRef}>
                <PromoPopupButton
                    iconName="check"
                    text={messages?.popupButtonOk}
                    onClick={(e) => {
                        e.preventDefault();
                        popupRef.current?.close();
                    }}
                />
            </PromoSimplePopup>,
        );
    };

    const infoPopup = (text) => {
        popupRef.current?.open(
            <PromoSimplePopup text={text} popupRef={popupRef}>
                <PromoPopupButton iconName="arrow-forward" target={'_blank'} href={termsHref} text={messages?.popupButtonTerms} />
                <PromoPopupButton
                    iconName="check"
                    text={messages?.popupButtonOk}
                    onClick={(e) => {
                        e.preventDefault();
                        popupRef.current?.close();
                    }}
                />
            </PromoSimplePopup>,
        );
    };

    return (
        <>
            <FeeTable>
                <FeeTableRow
                    $cursorPointer={isClickableFeeText}
                    onClick={() => {
                        if (isClickableFeeText) {
                            if (hasBonusSpecialCommission) {
                                return feePopup(improvedFeePopupText);
                            } else if (isRequestOperation) {
                                return feePopup(messages?.requestFeePopupText);
                            } else if (sendFeeText) {
                                return feePopup(sendFeeText);
                            }
                            return null;
                        }
                    }}
                >
                    <PromoText ellipsis color="gray80">
                        {messages?.feeText}
                    </PromoText>
                    <FeeTableSeparator />
                    <FeeTableRightBlock>
                        <PromoText color={hasBonusSpecialCommission ? 'primary' : 'gray80'}>
                            {isRequestOperation ? requestAmountTotal : fee}
                            {!isRequestOperation && feeAmount && !hasBonusSpecialCommission > 0 && messages?.includedFeeText}
                        </PromoText>
                        {isClickableFeeText && (
                            <PromoIcon name="info-rounded" color={hasBonusSpecialCommission ? 'primary' : 'gray80'} size="16px" leftIndent="8px" />
                        )}
                    </FeeTableRightBlock>
                </FeeTableRow>

                {sourceCurrency !== targetCurrency && (
                    <FeeTableRow
                        $cursorPointer
                        onClick={() => (hasBonusSpecialCommission ? feePopup(improvedFeePopupText) : infoPopup(messages?.sendRatePopupText))}
                    >
                        <PromoText ellipsis color="gray80">
                            {messages?.rateText}
                        </PromoText>
                        <FeeTableSeparator />
                        <FeeTableRightBlock $noShrink>
                            <PromoText color={bonusSpecialRate ? 'primary' : 'gray80'}>{props.rate}</PromoText>
                            <PromoIcon name="info-rounded" color={bonusSpecialRate ? 'primary' : 'gray80'} size="16px" leftIndent="8px" />
                        </FeeTableRightBlock>
                    </FeeTableRow>
                )}

                {processingTimeCaption && processingTimeCaption ? (
                    <FeeTableRow $cursorPointer onClick={() => infoPopup(setHTML(captions.processingDetails))}>
                        <PromoText color="gray80">{processingTimeCaption}</PromoText>
                        <FeeTableSeparator />
                        <FeeTableRightBlock>
                            <PromoText color="gray80" ellipsis>
                                {processingTimeEstimationCaption}
                            </PromoText>
                            <PromoIcon name="info-rounded" color="gray80" size="16px" leftIndent="8px" />
                        </FeeTableRightBlock>
                    </FeeTableRow>
                ) : null}
            </FeeTable>
            <PromoPopup ref={popupRef} />
        </>
    );
};

const mapStateToProps = ({ app, language, extra }, ownProps) => {
    const { calculator: calculatorData, ...landingPageData } = extra.strapiApi;

    return {
        calculatorData,
        landingPageData,
        countryCode: app.country,
        languageCode: language.lang,
        ...ownProps,
    };
};
export default connect(mapStateToProps)(FeeBlock);
